<script setup lang="ts">
import { Head } from '@inertiajs/vue3';
import { ClipboardCopy, Download } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { updateStyle } from '@/actions/App/Http/Controllers/CitationController';
import { store as referencesStore } from '@/actions/App/Http/Controllers/ReferencesController';
import BibliographyView from '@/components/BibliographyView.vue';
import ManualReferenceDialog from '@/components/references/ManualReferenceDialog.vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import { downloadBibTeX } from '@/lib/citations/bibtex';
import { copyBibliography, sortForStyle } from '@/lib/citations/formatters';
import type { CitationStyle } from '@/lib/citations/formatters';

interface Props {
    project: { id: string; name: string; citation_style: string };
    papers: ReferenceItem[];
    zotero: {
        connected: boolean;
        last_synced_at: string | null;
        collections: string[];
    };
}

const props = defineProps<Props>();

defineOptions({
    layout: (p: Props) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                { title: p.project.name, href: `/projects/${p.project.id}` },
                { title: 'Références', href: '#' },
            ],
        },
    ],
});

const toast = useToast();

// Show all papers
const allIds = computed<ReadonlySet<string>>(
    () => new Set(props.papers.map((p) => p.id)),
);
const paperOrder = computed<string[]>(() => props.papers.map((p) => p.id));

const lastSynced = props.zotero.last_synced_at
    ? new Date(props.zotero.last_synced_at).toLocaleDateString('fr-FR', {
          dateStyle: 'medium',
      })
    : null;

// Style selector
const styleOptions: { value: CitationStyle; label: string }[] = [
    { value: 'apa', label: 'APA 7' },
    { value: 'chicago', label: 'Chicago' },
    { value: 'vancouver', label: 'Vancouver' },
];
const localStyle = ref<CitationStyle>(
    (props.project.citation_style as CitationStyle) ?? 'apa',
);

function getCsrf(): string {
    return decodeURIComponent(
        document.cookie
            .split('; ')
            .find((c) => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1] ?? '',
    );
}

async function onStyleChange(newStyle: string) {
    if (newStyle === localStyle.value) {
        return;
    }

    const style = newStyle as CitationStyle;
    localStyle.value = style;

    try {
        const res = await fetch(updateStyle.url(props.project.id), {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': getCsrf(),
            },
            body: JSON.stringify({ style }),
        });

        if (!res.ok) {
            throw new Error('style update failed');
        }
    } catch {
        toast.error('Impossible de sauvegarder le style de citation.');
    }
}

async function onCopy() {
    const refs =
        localStyle.value === 'vancouver'
            ? props.papers
            : sortForStyle(props.papers, localStyle.value);

    try {
        await copyBibliography(refs, localStyle.value);
        toast.success(
            `Bibliographie ${localStyle.value.toUpperCase()} copiée dans le presse-papier.`,
        );
    } catch {
        toast.error('Impossible de copier dans le presse-papier.');
    }
}

function onExport() {
    downloadBibTeX(props.papers, props.project.name);
}
</script>

<template>
    <Head :title="`Références · ${project.name}`" />

    <div class="flex-1 overflow-y-auto">
        <div class="mx-auto max-w-3xl px-10 py-12">
            <!-- Page header -->
            <div class="mb-8 flex items-start justify-between">
                <div>
                    <p
                        class="mb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Références
                    </p>
                    <h1 class="mb-1 text-2xl font-bold tracking-tight">
                        Bibliographie
                    </h1>
                    <p class="text-sm text-muted-foreground">
                        Citations et références du projet.
                    </p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                    <select
                        :value="localStyle"
                        class="rounded-md border border-border bg-muted/50 px-2 py-1.5 text-xs text-foreground focus:ring-1 focus:ring-ring focus:outline-none"
                        @change="
                            onStyleChange(
                                ($event.target as HTMLSelectElement).value,
                            )
                        "
                    >
                        <option
                            v-for="opt in styleOptions"
                            :key="opt.value"
                            :value="opt.value"
                        >
                            {{ opt.label }}
                        </option>
                    </select>
                    <button
                        class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                        :disabled="!papers.length"
                        @click="onCopy"
                    >
                        <ClipboardCopy class="h-3 w-3" />
                        Copier
                    </button>
                    <button
                        class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                        :disabled="!papers.length"
                        @click="onExport"
                    >
                        <Download class="h-3 w-3" />
                        BibTeX
                    </button>
                </div>
            </div>

            <!-- Bibliography list -->
            <div
                v-if="papers.length === 0"
                class="py-12 text-center text-sm text-muted-foreground italic"
            >
                Aucune référence pour ce projet.
            </div>
            <BibliographyView
                v-else
                :references="papers"
                :cited-ids="allIds"
                :cited-order="paperOrder"
                :style="localStyle"
            />

            <!-- Add reference -->
            <div
                class="mt-6 flex items-center justify-between border-t border-border pt-4"
            >
                <ManualReferenceDialog
                    :project-id="project.id"
                    :store-url="referencesStore(project.id).url"
                />
                <div
                    v-if="zotero.connected"
                    class="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Zotero connecté
                    <span v-if="lastSynced">· Sync {{ lastSynced }}</span>
                </div>
                <Badge v-else variant="outline" class="text-xs"
                    >Zotero non connecté</Badge
                >
            </div>
        </div>
    </div>
</template>
